import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Sparkles } from 'lucide-react';
import { articlesApi } from '../../lib/api';

export const RelatedArticles = ({ currentSlug, category, internalLinks }) => {
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelated = async () => {
      setLoading(true);
      try {
        const res = await articlesApi.getRelated(currentSlug, 6);
        const data = res.data || [];
        if (data.length > 0) {
          setRelated(data);
        } else {
          // Fallback to category-based if no scored results
          const fallback = await articlesApi.getAll({ category, published: true });
          const articles = (fallback.data || [])
            .filter(a => a.slug !== currentSlug)
            .slice(0, 6)
            .map(a => ({ ...a, shared_topics: [], relevance_score: 0 }));
          setRelated(articles);
        }
      } catch {
        // Fallback on error
        try {
          const fallback = await articlesApi.getAll({ category, published: true });
          const articles = (fallback.data || [])
            .filter(a => a.slug !== currentSlug)
            .slice(0, 6)
            .map(a => ({ ...a, shared_topics: [], relevance_score: 0 }));
          setRelated(articles);
        } catch { /* silent */ }
      }
      setLoading(false);
    };
    if (currentSlug) fetchRelated();
  }, [currentSlug, category]);

  if (loading) {
    return (
      <div className="mt-16 pt-12 border-t border-zinc-800" data-testid="related-articles-section">
        <h3 className="text-2xl text-white font-heading mb-8 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-[#00F0FF]" />
          Recommended Reading
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-zinc-900/50 border border-zinc-800 animate-pulse">
              <div className="h-48 bg-zinc-800" />
              <div className="p-5 space-y-3">
                <div className="h-4 bg-zinc-800 rounded w-3/4" />
                <div className="h-3 bg-zinc-800 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (related.length === 0) return null;

  return (
    <div className="mt-16 pt-12 border-t border-zinc-800" data-testid="related-articles-section">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl text-white font-heading flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-[#00F0FF]" />
          Recommended Reading
        </h3>
        <span className="text-xs text-zinc-600 uppercase tracking-widest hidden sm:block">
          Based on topic overlap
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {related.map((article, idx) => (
          <Link
            key={article.slug}
            to={`/blogs/article/${article.slug}`}
            className="group block bg-zinc-900/30 border border-zinc-800 hover:border-[#00F0FF]/40 transition-all duration-500 overflow-hidden"
            data-testid={`related-article-${article.slug}`}
            style={{ animationDelay: `${idx * 100}ms` }}
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src={article.featured_image}
                alt={article.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <span className="absolute top-3 left-3 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#E63946] text-white font-bold">
                {article.category}
              </span>
              {article.relevance_score > 20 && (
                <span className="absolute top-3 right-3 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30 backdrop-blur-sm">
                  Top Match
                </span>
              )}
            </div>
            <div className="p-5">
              <h4 className="text-white text-base font-heading leading-tight group-hover:text-[#00F0FF] transition-colors duration-300 line-clamp-2 mb-3">
                {article.title}
              </h4>
              {article.shared_topics && article.shared_topics.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {article.shared_topics.slice(0, 3).map(topic => (
                    <span
                      key={topic}
                      className="px-2 py-0.5 text-[10px] bg-zinc-800/80 text-[#00F0FF]/80 border border-zinc-700/50 uppercase tracking-wider"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-zinc-500 text-sm line-clamp-2 mb-3 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-zinc-600 text-xs">
                  <Clock className="w-3 h-3" />
                  <span>{article.read_time} min read</span>
                </div>
                <span className="text-[#00F0FF] text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Read <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
      <div className="mt-8 text-center">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 px-6 py-2.5 text-sm text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/10 transition-all duration-300"
          data-testid="browse-all-articles-link"
        >
          Browse All Articles <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

export default RelatedArticles;
